import API_CONFIG, { getApiUrl, getAuthHeaders, replaceUrlParams } from '../config/api';

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  try {
    const url = getApiUrl(endpoint);
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...getAuthHeaders(),
        ...options.headers
      }
    });
    
    clearTimeout(timeoutId);

    // Check if response is HTML (error page) instead of JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.warn(`API returned non-JSON response for ${endpoint}. Content-Type: ${contentType}`);
      throw new Error(`API endpoint ${endpoint} is not available or returned HTML instead of JSON`);
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `API request failed with status ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Authentication API calls
export const authAPI = {
  login: async (credentials) => {
    return apiCall(API_CONFIG.ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }
};

// Contact Messages API calls
export const contactAPI = {
  submit: async (contactData) => {
    return apiCall(API_CONFIG.ENDPOINTS.CONTACT_SUBMIT, {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  },

  getAll: async (page = 1, status = null) => {
    let endpoint = `${API_CONFIG.ENDPOINTS.CONTACT_MESSAGES}?page=${page}`;
    if (status) {
      endpoint += `&status=${status}`;
    }
    return apiCall(endpoint);
  },

  updateStatus: async (id, status) => {
    const endpoint = replaceUrlParams(API_CONFIG.ENDPOINTS.CONTACT_STATUS, { id });
    return apiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }
};

// Consultation Requests API calls
export const consultationAPI = {
  submit: async (consultationData) => {
    return apiCall(API_CONFIG.ENDPOINTS.CONSULTATION_SUBMIT, {
      method: 'POST',
      body: JSON.stringify(consultationData)
    });
  },

  getAll: async (page = 1, status = null) => {
    let endpoint = `${API_CONFIG.ENDPOINTS.CONSULTATION_REQUESTS}?page=${page}`;
    if (status) {
      endpoint += `&status=${status}`;
    }
    return apiCall(endpoint);
  },

  updateStatus: async (id, status) => {
    const endpoint = replaceUrlParams(API_CONFIG.ENDPOINTS.CONSULTATION_STATUS, { id });
    return apiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }
};

// Languages API calls
export const languagesAPI = {
  getAll: async () => {
    return apiCall(API_CONFIG.ENDPOINTS.LANGUAGES);
  },

  getActive: async () => {
    return apiCall(API_CONFIG.ENDPOINTS.LANGUAGES_ACTIVE);
  },

  toggle: async (code) => {
    const endpoint = replaceUrlParams(API_CONFIG.ENDPOINTS.LANGUAGE_TOGGLE, { code });
    return apiCall(endpoint, {
      method: 'PUT'
    });
  }
};

// Dashboard API calls
export const dashboardAPI = {
  getStats: async () => {
    return apiCall(API_CONFIG.ENDPOINTS.DASHBOARD_STATS);
  }
};

// Auth utilities
export const authUtils = {
  getToken: () => localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY),
  setToken: (token) => localStorage.setItem(API_CONFIG.AUTH.TOKEN_KEY, token),
  removeToken: () => localStorage.removeItem(API_CONFIG.AUTH.TOKEN_KEY),
  
  getUser: () => {
    const user = localStorage.getItem(API_CONFIG.AUTH.USER_KEY);
    return user ? JSON.parse(user) : null;
  },
  setUser: (user) => localStorage.setItem(API_CONFIG.AUTH.USER_KEY, JSON.stringify(user)),
  removeUser: () => localStorage.removeItem(API_CONFIG.AUTH.USER_KEY),
  
  isAuthenticated: () => {
    return !!localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);
  },
  
  logout: () => {
    localStorage.removeItem(API_CONFIG.AUTH.TOKEN_KEY);
    localStorage.removeItem(API_CONFIG.AUTH.USER_KEY);
  }
};
